import isArray from 'lodash/isArray';
import { isValidGUID, parseOrNull } from '../util/general';
import { isObject } from '@mimir/primitive/type-guards/isObject';
import type { ItemEditableMaterials } from './itemEditStatusDto';
import type { ScheduledRecordingDto, RecordingSeriesSmallDto } from './liveRecordingDtos';
import type { ExpectedPipelineDto, DynamicSourceListForTenantDto } from './liveSourceDtos';
import type { NdiSourcesForTenantDto } from './ndiDtos';
import type { FieldValue } from './fieldValue';
import type { SystemDefaultField } from '../mdf/mdfDefaults';
import type { PlayoutDeviceStatusGetDto } from './playoutDtos';
import type { ItemAccessControl } from './itemAccessControlDto';
import type { LanguageCode } from './asyncOperationItemCreationTypes';
import type { DynamoItemType } from './itemTypes';
import type { FolderWsDto } from './folderModels';
import type { HelpChatTokenBody, HelpChatEndBody, HelpChatErrorBody } from './helpChatDtos';
import isString from 'lodash/isString';
import type { ProgressInfo } from './progress';
import type { Logger } from '../types/logger';

const folderMessageTypes = <const>[
  'folder_created',
  'folder_deleted',
  'folder_updated',
];

// For messages sent from the server
const messageTypes = [
  'items_created',
  'items_deleted',
  'item_metadata_updated',
  'upload_lock_obtained',
  'recording_item_state',
  'recording_series_state',
  'pipeline_update',
  'ndi_sources_update',
  'dynamic_source_list_update',
  'location_update',
  'router_source_update',
  'router_controller_status_update',
  'user_settings_update',
  'recording_unscheduled',
  'item_edit_status',
  'playout_status_update',
  'mode_update_failed',
  'mode_updated',
  'comment_created',
  'comment_updated',
  'item_state_change', // This message type is supposed to be a generic event for all item state change and should be used going forward
  'item_version_change',
  'notify_client',
  'add_item_to_folder',
  'remove_item_from_folder',
  'transcript_updated',
  // Help-chat streaming: one token frame per Bedrock delta, a single end frame
  // with citations/grounded, or an error frame. All carry clientRequestId.
  'help_chat_token',
  'help_chat_end',
  'help_chat_error',
  ...folderMessageTypes,
] as const;
export type SupportedMessage = typeof messageTypes[number];

type KnownData =
  SlimItemCreationResponse[] |
  ItemsDeletedMessage |
  WebsocketMetadataMessage |
  LockObtained |
  RecordingItemMessage |
  RecordingSeriesMessage |
  RecordingUnscheduled |
  SetWorkFolderMessage |
  ItemEditStatusMessage |
  PlayoutDeviceStatusGetDto |
  ExpectedPipelineDto[] |
  NdiSourcesForTenantDto |
  DynamicSourceListForTenantDto |
  ClientAgentHelloDto |
  ModeUpdatedMessage |
  FolderWsDto |
  FolderDeleteWSMessage |
  CommentCreatedMessage |
  CommentUpdatedMessage |
  ItemVersionChangeMessage |
  AddItemToFolderMessage |
  RemoveItemFromFolderMessage |
  HelpChatTokenWsMessage |
  HelpChatEndWsMessage |
  HelpChatErrorWsMessage |
  NotifyClientMessage;

export type ItemsDeletedMessage = string[];
export type RecordingUnscheduled = { itemId: string };

export type SlimItemCreationResponse = {
  id: string,
  folderParents: string[],
  itemType: DynamoItemType,
  metadata: {
    formId: string,
    formData: {
      [SystemDefaultField.Title]: FieldValue,
      [SystemDefaultField.CreatedOn]: FieldValue,
    }
  },
  progressInfo: ProgressInfo | undefined,
  visibleTo: ItemAccessControl | undefined;
  updatableBy: ItemAccessControl | undefined;
  createdBy: string | undefined;
  languageCode: LanguageCode | undefined;
};

export type CommentCreatedMessage = {
  itemId: string;
  commentId: string;
};

export type CommentUpdatedMessage = {
  itemId: string;
  commentId: string;
  assignedUserId: string | null;
};

export type WebsocketMetadataMessage = {
  id: string;
  // To avoid potential data leak, we don't want to send the metadata to the clients
  metadata: undefined;
  /**
   * the updated metadataVersion of the item
   */
  metadataVersion: number;
};

const addItemToFolderOperations = <const>['add', 'move'];
type AddItemToFolderOperation = typeof addItemToFolderOperations[number];

export type AddItemToFolderMessage = {
  itemId: string;
  /**
   * The folder ID from which the item is being moved.
   *
   * This is only used when the operationType is 'move'.
   * It is `undefined` when the operationType is 'add'.
   */
  sourceFolderId: string | undefined;
  /**
   * The folder id where the item is being copied or moved to
   */
  targetFolderId: string;
  operationType: AddItemToFolderOperation;
};

export type RemoveItemFromFolderMessage = {
  itemId: string;
  /**
   * The folder id where the item is being removed from
   */
  sourceFolderId: string;
};

export type ItemVersionChangeMessage = {
  oldVersionId: string,
  newVersionId: string,
};

type LockObtained = { id: string };

// This message is used to send out the state of a scheduled recording.
// The item may be a newly created recording or an existing one that
// has been modified.
export type RecordingItemMessage = {
  item: ScheduledRecordingDto;
};

// This message is used to send out the state of a recording series.
// The series may be newly created or an existing one that has been modified.
export type RecordingSeriesMessage = {
  series: RecordingSeriesSmallDto;
};

export type SetWorkFolderMessage = {
  workFolderId: string | null;
};

export interface WebsocketMessage {
  type: SupportedMessage;
  data?: KnownData;
}

export interface ItemStateChangeMessage {
  itemId: string;
}

export type TranscriptUpdatedMessage = ItemStateChangeMessage;

export const isSetWorkFolderMessageType = (x: unknown): x is SetWorkFolderMessage => {
  if (!isObject(x)) return false;
  if (!x.hasOwnProperty('workFolderId')) return false;

  const probablyWorkFolderMsg = x as SetWorkFolderMessage;
  return typeof probablyWorkFolderMsg.workFolderId === 'string' || probablyWorkFolderMsg.workFolderId == null;
};

export const isWebsocketMetadataMessageType = (body: unknown): body is WebsocketMetadataMessage => {
  if (!isObject(body)) return false;

  const probablyWsMetadata = body as WebsocketMetadataMessage;
  return typeof probablyWsMetadata.id === 'string' && typeof probablyWsMetadata.metadataVersion === 'number';
};

export const isAddItemToFolderMessageType = (body: unknown): body is AddItemToFolderMessage => {
  if (!isObject(body)) return false;

  const probablyAddItemToFolder = body as AddItemToFolderMessage;
  return typeof probablyAddItemToFolder.itemId === 'string' &&
    typeof probablyAddItemToFolder.targetFolderId === 'string' &&
    (typeof probablyAddItemToFolder.sourceFolderId === 'string' || probablyAddItemToFolder.sourceFolderId === undefined) &&
    addItemToFolderOperations.includes(probablyAddItemToFolder.operationType);
};

export const isRemoveItemFromFolderMessageType = (body: unknown): body is RemoveItemFromFolderMessage => {
  if (!isObject(body)) return false;

  const probablyRemoveItemFromFolder = body as RemoveItemFromFolderMessage;
  return typeof probablyRemoveItemFromFolder.itemId === 'string' &&
    typeof probablyRemoveItemFromFolder.sourceFolderId === 'string';
};

export const isUploadLockObtained = (body: unknown): body is LockObtained =>  {
  if (!isObject(body)) {
    return false;
  }
  const probablyLockObtained = body as Partial<LockObtained>;
  return typeof probablyLockObtained.id === 'string';
};

export const isItemCreationResponses = (body: unknown): body is SlimItemCreationResponse[] => {
  if (!isArray(body)) {
    return false;
  }
  return body.every(isSlimItemCreationResponse);
};

export const isSlimItemCreationResponse = (body: unknown): body is SlimItemCreationResponse => {
  if (!isObject(body)) {
    return false;
  }
  const probablyResponse = body as Partial<SlimItemCreationResponse>;
  return typeof probablyResponse.id === 'string' && isValidGUID(probablyResponse.id) &&
         typeof probablyResponse.itemType === 'string' && probablyResponse.metadata !== undefined;
};

export const isItemsDeletedMessage = (body: unknown): body is ItemsDeletedMessage => {
  return Array.isArray(body) && body.every(id => typeof id === 'string');
};

export const isRecordingUnscheduledMessage = (body: unknown): body is RecordingUnscheduled => {
  if (!isObject(body)) {
    return false;
  }
  const obj = body as RecordingUnscheduled;
  return !!(obj.itemId);
};

export const isRecordingItemMessage = (body: unknown): body is RecordingItemMessage => {
  if (!isObject(body)) {
    return false;
  }
  const obj = body as RecordingItemMessage;
  return !!(obj.item?.id && obj.item.startTimeMs);
};

export const isRecordingSeriesMessage = (body: unknown): body is RecordingSeriesMessage => {
  if (!isObject(body)) {
    return false;
  }
  const obj = body as RecordingSeriesMessage;
  return !!(obj.series?.id && obj.series.recordingItems);
};

export const isPlayoutDeviceStatusGetDto = (body: unknown): body is PlayoutDeviceStatusGetDto => {
  if (!isObject(body)) {
    return false;
  }
  const obj = body as Partial<PlayoutDeviceStatusGetDto>;
  return !!(obj.playoutDeviceId) && !!(obj.status);
};

export const isWebSocketMessage = (obj: unknown): obj is WebsocketMessage => {
  if (!isObject(obj)) return false;
  const probablyWsMessage = obj as Partial<WebsocketMessage>;
  return typeof probablyWsMessage.type === 'string' && messageTypes.includes(probablyWsMessage.type);
};

export const parseWebsocketMessage = (logger: Logger, messageString: string): WebsocketMessage | null => {
  const result: unknown = parseOrNull(logger, messageString);
  if (!isWebSocketMessage(result)) {
    return null;
  } else {
    return result;
  }
};

export type ItemEditStatusMessage = {
  itemId: string;
  statuses: {
    uId: string;
    material: ItemEditableMaterials;
  }[];
};
export const isItemEditStatusMessage = (obj: unknown): obj is ItemEditStatusMessage => {
  if (!isObject(obj)) return false;
  const possibleMsg = obj as Partial<ItemEditStatusMessage>;
  return !!(
    possibleMsg.itemId
    && possibleMsg.statuses && Array.isArray(possibleMsg.statuses)
    && possibleMsg.statuses.every(st => !!(st.material && st.uId))
  );
};

export const isItemStateChangeMessage = (obj: unknown): obj is ItemStateChangeMessage => {
  if (!isObject(obj)) return false;
  const possibleItemStateChangeMsg = obj as Partial<ItemStateChangeMessage>;
  return typeof possibleItemStateChangeMsg.itemId === 'string';
};

export const isTranscriptUpdatedMessage = (obj: unknown): obj is TranscriptUpdatedMessage => {
  return isItemStateChangeMessage(obj);
};

export const isItemVersionChangeMessage = (obj: unknown): obj is ItemVersionChangeMessage => {
  if (!isObject(obj)) return false;
  const possibleItemVersionChangeMsg = obj as Partial<ItemVersionChangeMessage>;
  return (typeof possibleItemVersionChangeMsg.newVersionId === 'string')
    && (typeof possibleItemVersionChangeMsg.oldVersionId === 'string');
};

export const isCommentMessage = (obj: unknown): obj is CommentCreatedMessage | CommentUpdatedMessage => {
  if (!isObject(obj)) return false;
  const possibleCommentCreatedMsg = obj as Partial<CommentCreatedMessage | CommentUpdatedMessage>;
  return (typeof possibleCommentCreatedMsg.itemId === 'string') &&  (typeof possibleCommentCreatedMsg.commentId === 'string');
};

export interface ClientAgentHelloDto {
  agentType: 'mimirClient';
  mode?: ClientWsMode;
  action: 'add' |'remove' | 'keep-alive'
}

export function isClientAgentHelloDto(value: unknown): value is ClientAgentHelloDto {
  if (value == null || typeof value !== 'object') {
    return false;
  }
  const obj = value as Partial<ClientAgentHelloDto>;
  return obj.agentType === 'mimirClient';
}

export type ClientWsMode = 'scheduler' | 'playout' | 'general';

export type ModeUpdatedMessage = {
  mode: ClientWsMode,
};

export type FolderDeleteWSMessage = { folderId: string };

export function isInstanceOfFolderDeleteWSMessage(dto: unknown): dto is FolderDeleteWSMessage {
  if (!isObject(dto)) return false;
  const possibleDto = dto as Partial<FolderDeleteWSMessage>;

  return isString(possibleDto.folderId);
}

export type NewNotificationMessage = {
  type: 'new_notification';
  notificationId: string;
};

export type MarkAsReadNotificationMessage = {
  type: 'mark_as_read';
  notificationId: string;
};

export type MarkAllAsReadNotificationMessage = {
  type: 'mark_all_as_read';
  catchupTimestamp: string;
};

export type NotifyClientMessage = NewNotificationMessage | MarkAsReadNotificationMessage | MarkAllAsReadNotificationMessage;

// Help-chat streaming WebSocket payloads: each is a shared HelpChatStreamEvent
// body (helpChatDtos.ts) plus the clientRequestId the client uses to route the
// frame to the originating chat instance/tab. Reusing the bodies keeps the
// server stream events and these frames from drifting on field names.
export type HelpChatTokenWsMessage = { clientRequestId: string } & HelpChatTokenBody;
export type HelpChatEndWsMessage = { clientRequestId: string } & HelpChatEndBody;
export type HelpChatErrorWsMessage = { clientRequestId: string } & HelpChatErrorBody;

function hasClientRequestId(value: unknown): value is { clientRequestId: string } {
  return isObject(value) && typeof (value as { clientRequestId?: unknown }).clientRequestId === 'string';
}

export const isHelpChatTokenWsMessage = (value: unknown): value is HelpChatTokenWsMessage =>
  hasClientRequestId(value) && typeof (value as HelpChatTokenWsMessage).text === 'string';

export const isHelpChatEndWsMessage = (value: unknown): value is HelpChatEndWsMessage =>
  hasClientRequestId(value)
  && Array.isArray((value as HelpChatEndWsMessage).citations)
  && typeof (value as HelpChatEndWsMessage).grounded === 'boolean';

export const isHelpChatErrorWsMessage = (value: unknown): value is HelpChatErrorWsMessage =>
  hasClientRequestId(value) && typeof (value as HelpChatErrorWsMessage).message === 'string';
